import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  Slider,
  Stack,
  Button,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  // ClosedCaption as CaptionIcon,
  SkipNext as SkipNextIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import TutorialTip from './TutorialTip';
import { getNextTutorialFeature, getTutorialConfig } from '../../../config/tutorials.config';

const VideoContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  backgroundColor: '#000',
  borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  boxShadow: theme.shadows[8],
  marginBottom: theme.spacing(3),
  '& video': {
    width: '100%',
    height: 'auto',
    display: 'block'
  },
  // Ensure next video overlay appears above controls when video ends
  '& .next-video-overlay': {
    zIndex: 15
  }
}));

const ControlsOverlay = styled(Box)(({ theme, show }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: show 
    ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)' 
    : 'transparent',
  padding: theme.spacing(2.5),
  transition: 'opacity 0.3s ease-in-out',
  opacity: show ? 1 : 0,
  pointerEvents: show ? 'auto' : 'none',
  zIndex: 10
}));

const ControlButton = styled(IconButton)(({ theme }) => ({
  color: '#fff',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    transform: 'scale(1.1)'
  }
}));

// Next Video Overlay - appears inside video container when video ends
const NextVideoOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.85) 50%, transparent 100%)',
  padding: theme.spacing(3, 2.5),
  zIndex: 15,
  animation: 'slideUp 0.4s ease-out',
  '@keyframes slideUp': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: 0
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1
    }
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 2)
  }
}));

const NextVideoContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  maxWidth: '100%',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5)
  }
}));

const NextVideoActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'column',
    '& button': {
      width: '100%'
    }
  }
}));

const NextVideoInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0
}));

const NextVideoLabel = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5)
}));

const NextVideoTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: 1.3,
  marginBottom: theme.spacing(0.5),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem'
  }
}));

const NextVideoDescription = styled(Typography)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.75)',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: 1.4,
  marginTop: theme.spacing(0.5),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem'
  }
}));

const NextVideoButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1.25, 2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  fontSize: '0.9rem',
  minWidth: 140,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: 'auto'
  }
}));

const ReplayButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  color: '#fff',
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1.25, 2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  fontSize: '0.9rem',
  minWidth: 140,
  border: '1px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'scale(1.05)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4)',
    borderColor: 'rgba(255, 255, 255, 0.5)'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    minWidth: 'auto'
  }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  zIndex: '10000 !important',
  '& .MuiDialog-container': {
    zIndex: 10000
  },
  '& .MuiDialog-paper': {
    borderRadius: theme.shape.borderRadius * 3,
    maxWidth: 1000,
    minWidth: 800,
    overflow: 'hidden',
    boxShadow: theme.shadows[24],
    background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
    zIndex: 10000,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      margin: theme.spacing(2),
      maxWidth: 'calc(100% - 32px)'
    }
  },
  '& .MuiBackdrop-root': {
    zIndex: 9999
  },
  '& .MuiDialogContent-root': {
    zIndex: 10001,
    position: 'relative',
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2)
    }
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: '#fff',
  padding: theme.spacing(3, 3, 2.5, 3),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 1,
    background: 'rgba(255, 255, 255, 0.1)'
  }
}));

/**
 * Tutorial Video Player Component
 * Accessible video player with captions, transcript, and keyboard controls
 */
const TutorialVideoPlayer = ({ open, onClose, videoSrc, title, transcript, onWatched, featureId, onNextVideo }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [nextTutorial, setNextTutorial] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [speedMenuAnchor, setSpeedMenuAnchor] = useState(null);
  const controlsTimeoutRef = useRef(null);
  const prevVideoSrcRef = useRef(videoSrc);

  // Load next tutorial when component mounts, featureId changes, or video ends
  // This ensures we always show the next unwatched video
  const loadNextTutorial = useCallback(async () => {
    if (!featureId) {
      setNextTutorial(null);
      return;
    }
    
    try {
      const nextFeatureId = await getNextTutorialFeature(featureId);
      if (nextFeatureId) {
        const nextTutorialConfig = getTutorialConfig(nextFeatureId);
        if (nextTutorialConfig) {
          setNextTutorial(nextTutorialConfig);
        } else {
          setNextTutorial(null);
        }
      } else {
        // No next tutorial available
        setNextTutorial(null);
      }
    } catch (error) {
      console.error('Error loading next tutorial:', error);
      setNextTutorial(null);
    }
  }, [featureId]);

  // Handle video source changes smoothly (for next video transitions)
  useEffect(() => {
    if (prevVideoSrcRef.current !== null && prevVideoSrcRef.current !== videoSrc && open && videoRef.current) {
      // Video source changed - smooth transition
      setIsTransitioning(true);
      const video = videoRef.current;
      
      // Reset video state for new video
      video.pause();
      video.currentTime = 0;
      setCurrentTime(0);
      setVideoEnded(false);
      setPlaying(false);
      setShowControls(true);
      setDuration(0); // Reset duration for new video
      
      // Load new video source
      video.load();
      
      // Reload next tutorial for the new video (to ensure we show next unwatched)
      loadNextTutorial();
      
      // Small delay to allow smooth transition, then auto-play new video
      const transitionTimer = setTimeout(() => {
        setIsTransitioning(false);
        prevVideoSrcRef.current = videoSrc;
        
        // Auto-play the new video after transition
        if (video && open) {
          video.play().then(() => {
            setPlaying(true);
          }).catch(() => {
            setPlaying(false);
          });
        }
      }, 300);
      
      return () => {
        clearTimeout(transitionTimer);
      };
    } else if (prevVideoSrcRef.current === null && videoSrc && open) {
      // First time opening - just set the ref
      prevVideoSrcRef.current = videoSrc;
    } else if (!open) {
      prevVideoSrcRef.current = null;
      setIsTransitioning(false);
    }
  }, [videoSrc, open, loadNextTutorial]);

  // Reset and auto-play when dialog opens
  useEffect(() => {
    if (!open) {
      // Pause video when dialog closes
      const video = videoRef.current;
      if (video) {
        video.pause();
        setPlaying(false);
      }
      setIsTransitioning(false);
      prevVideoSrcRef.current = null;
      return;
    }

    let isMounted = true;
    let eventCleanup = null;
    let timeoutId = null;
    
    // Wait for dialog to be fully rendered before accessing video element
    const timer = setTimeout(() => {
      const video = videoRef.current;
      if (!video || !open || !isMounted) return;

      // Only reset if this is a new video (not transitioning)
      if (!isTransitioning) {
        video.currentTime = 0;
        setCurrentTime(0);
        setVideoEnded(false);
        setNextTutorial(null);
      }
      // Note: Duration will be set by event listeners when metadata loads
      
      // Auto-play video when dialog opens (user already clicked Play in prompt)
      const playVideo = async () => {
        if (!isMounted || !video) return;
        
        try {
          // Ensure video is ready
          if (video.readyState < 2) {
            // Wait for video to have enough data
            await new Promise((resolve) => {
              if (!isMounted || !video) {
                resolve();
                return;
              }
              
              const handleCanPlay = () => {
                if (video) {
                  video.removeEventListener('canplay', handleCanPlay);
                  video.removeEventListener('loadeddata', handleCanPlay);
                }
                resolve();
              };
              
              video.addEventListener('canplay', handleCanPlay);
              video.addEventListener('loadeddata', handleCanPlay);
              
              // Store cleanup
              eventCleanup = () => {
                if (video) {
                  video.removeEventListener('canplay', handleCanPlay);
                  video.removeEventListener('loadeddata', handleCanPlay);
                }
              };
              
              // Fallback timeout
              timeoutId = setTimeout(() => {
                if (video) {
                  video.removeEventListener('canplay', handleCanPlay);
                  video.removeEventListener('loadeddata', handleCanPlay);
                }
                resolve();
              }, 3000);
            });
          }
          
          if (isMounted && video) {
            await video.play();
            if (isMounted) {
              setPlaying(true);
            }
          }
        } catch (error) {
          // Auto-play may fail due to browser policies, user can click to play
          if (isMounted) {
            setPlaying(false);
          }
        }
      };
      
      // Try to play immediately if video is ready
      if (video.readyState >= 2) {
        playVideo();
      } else {
        // Wait for video to load
        const handleCanPlay = () => {
          if (isMounted) {
            playVideo();
          }
          if (video) {
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('loadeddata', handleCanPlay);
          }
        };
        
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('loadeddata', handleCanPlay);
        
        eventCleanup = () => {
          if (video) {
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('loadeddata', handleCanPlay);
          }
        };
        
        // Also try to load the video if it hasn't started loading
        if (video.readyState === 0) {
          video.load();
        }
      }
    }, 100); // Small delay to ensure dialog is rendered

    return () => {
      isMounted = false;
      clearTimeout(timer);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (eventCleanup) {
        eventCleanup();
      }
    };
  }, [open, videoSrc, isTransitioning]);

  // Ensure duration is loaded when videoSrc changes
  useEffect(() => {
    if (!open) return;
    
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    // Force reload metadata when videoSrc changes
    const loadDuration = () => {
      if (video.duration && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      } else {
        // Try to load metadata
        video.load();
        
        // Check again after a delay
        setTimeout(() => {
          if (video.duration && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
            setDuration(video.duration);
          }
        }, 200);
      }
    };

    // Wait for video element to be ready
    const timer = setTimeout(() => {
      loadDuration();
    }, 150);

    return () => {
      clearTimeout(timer);
    };
  }, [videoSrc, open]);

  // Load next tutorial when featureId changes
  useEffect(() => {
    loadNextTutorial();
  }, [loadNextTutorial]);

  // Update current time and handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !open) {
      // Reset state when dialog closes
      setCurrentTime(0);
      setDuration(0);
      return;
    }

    const handleTimeUpdate = () => {
      if (video && video.currentTime !== undefined && !isNaN(video.currentTime) && isFinite(video.currentTime) && video.currentTime >= 0) {
        // Direct state update for immediate response
        setCurrentTime(video.currentTime);
      }
    };

    const updateDuration = () => {
      if (video && video.duration !== undefined && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
    };

    const handleLoadedMetadata = () => {
      updateDuration();
      // Also update currentTime if available
      if (video && video.currentTime !== undefined && !isNaN(video.currentTime)) {
        setCurrentTime(video.currentTime);
      }
    };

    const handleLoadedData = () => {
      updateDuration();
    };

    const handleDurationChange = () => {
      updateDuration();
    };

    const handleCanPlay = () => {
      updateDuration();
    };

    const handleCanPlayThrough = () => {
      updateDuration();
    };

    const handlePlaying = () => {
      // Ensure state is synced when video starts playing
      if (video && video.currentTime !== undefined && !isNaN(video.currentTime)) {
        setCurrentTime(video.currentTime);
      }
      setPlaying(true);
    };

    const handlePause = () => {
      // Ensure state is synced when video pauses
      if (video && video.currentTime !== undefined && !isNaN(video.currentTime)) {
        setCurrentTime(video.currentTime);
      }
      setPlaying(false);
    };

    const handleEnded = () => {
      setPlaying(false);
      setCurrentTime(video.duration || 0);
      setVideoEnded(true);
      setShowControls(false); // Hide controls when video ends to show next video overlay
      if (onWatched) {
        onWatched();
      }
      // Reload next tutorial when video ends to ensure we show the next unwatched video
      // This is important because the user might have just watched the "next" video
      // and we need to find the next unwatched one
      if (featureId) {
        loadNextTutorial();
      }
    };

    // Add event listeners with passive option for better performance
    video.addEventListener('timeupdate', handleTimeUpdate, { passive: true });
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('playing', handlePlaying);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    // Check if duration is already available
    if (video.duration && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
      setDuration(video.duration);
    }

    // Check if currentTime is already available (in case video was already playing)
    if (video.currentTime !== undefined && !isNaN(video.currentTime)) {
      setCurrentTime(video.currentTime);
    }

    // Force load metadata if not already loaded
    if (video.readyState >= 1) {
      // Check duration after a short delay to allow metadata to load
      setTimeout(() => {
        updateDuration();
        if (video && video.currentTime !== undefined && !isNaN(video.currentTime)) {
          setCurrentTime(video.currentTime);
        }
      }, 100);
    } else {
      video.load(); // Force video to load metadata
    }

    // Fallback: Poll for duration if it's still not available after a delay
    const durationPollInterval = setInterval(() => {
      if (video && video.duration && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
        clearInterval(durationPollInterval);
      }
    }, 500);

    // Fallback: Poll for currentTime updates (in case timeupdate event is throttled or not firing)
    // This ensures the progress bar updates even if the event is throttled
    // Only poll when video is playing to avoid unnecessary updates
    const timePollInterval = setInterval(() => {
      if (video && !video.paused && !video.ended && video.currentTime !== undefined && !isNaN(video.currentTime) && isFinite(video.currentTime)) {
        const newTime = video.currentTime;
        // Always update - let React handle batching
        setCurrentTime(newTime);
      }
    }, 250); // Poll every 250ms as fallback (timeupdate fires ~4 times per second)

    // Clear duration polling after 10 seconds (duration should be loaded by then)
    const durationPollTimeout = setTimeout(() => {
      clearInterval(durationPollInterval);
    }, 10000);

    return () => {
      // Clean up all event listeners
      if (video) {
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('durationchange', handleDurationChange);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('canplaythrough', handleCanPlayThrough);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('ended', handleEnded);
      }
      // Clean up all intervals and timeouts
      clearInterval(durationPollInterval);
      clearInterval(timePollInterval);
      clearTimeout(durationPollTimeout);
    };
  }, [open, videoSrc, onWatched, featureId, loadNextTutorial]);

  // Auto-hide controls
  useEffect(() => {
    // Don't auto-show controls if video has ended (next video overlay should show instead)
    if (videoEnded) {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      setShowControls(false);
      return;
    }

    if (playing) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    } else {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      setShowControls(true);
    }

    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [playing, videoEnded]);

  // Fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Update video volume
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.volume = volume;
      videoRef.current.muted = muted;
    }
  }, [volume, muted]);

  // Update video playback rate
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setPlaying((prevPlaying) => {
      const shouldPlay = !prevPlaying;
      if (shouldPlay) {
        const playPromise = video.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      } else {
        video.pause();
      }
      return shouldPlay;
    });
  }, []);

  const handleSeek = (event, newValue) => {
    if (videoRef.current) {
      videoRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }, [isFullscreen]);

  const handleSpeedMenuOpen = (event) => {
    setSpeedMenuAnchor(event.currentTarget);
  };

  const handleSpeedMenuClose = () => {
    setSpeedMenuAnchor(null);
  };

  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
    handleSpeedMenuClose();
  };

  const speedOptions = [1, 1.25, 1.5, 1.75, 2];

  // Keyboard controls - placed after function definitions
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open || !videoRef.current) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(Math.min(1, volume + 0.1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(Math.max(0, volume - 0.1));
          break;
        case 'm':
          e.preventDefault();
          setMuted(!muted);
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
        default:
          break;
      }
    };

    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, playing, volume, muted, duration, togglePlay, toggleFullscreen]);

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNextVideo = () => {
    if (nextTutorial && onNextVideo) {
      onNextVideo(nextTutorial);
    }
  };

  const handleReplay = () => {
    const video = videoRef.current;
    if (video) {
      // Reset video to beginning
      video.currentTime = 0;
      setCurrentTime(0);
      setVideoEnded(false);
      setShowControls(true);
      
      // Play the video
      video.play().then(() => {
        setPlaying(true);
      }).catch(() => {
        setPlaying(false);
      });
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (playing) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth={false}
      slotProps={{
        backdrop: {
          sx: { zIndex: 9999 }
        }
      }}
      sx={{
        zIndex: 10000,
        '& .MuiDialog-container': {
          zIndex: 10000
        }
      }}
    >
      <StyledDialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <PlayIcon sx={{ fontSize: 28, color: '#fff' }} />
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: '#fff',
                lineHeight: 1.2
              }}
            >
              {title || 'Tutorial Video'}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close"
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </StyledDialogTitle>
      <DialogContent>
        <Box
          ref={containerRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => {
            if (playing) {
              setShowControls(false);
            }
          }}
          sx={{
            position: 'relative'
          }}
        >
          <VideoContainer
            sx={{
              cursor: 'pointer'
            }}
          >
            <video
              ref={videoRef}
              src={videoSrc}
              preload="auto"
              autoPlay
              playsInline
              style={{ width: '100%', height: 'auto' }}
              onClick={togglePlay}
              onTimeUpdate={(e) => {
                // Inline handler as backup - ensures time updates even if event listener fails
                const video = e.target;
                if (video && video.currentTime !== undefined && !isNaN(video.currentTime) && isFinite(video.currentTime) && video.currentTime >= 0) {
                  setCurrentTime(video.currentTime);
                }
              }}
              onLoadedMetadata={(e) => {
                const video = e.target;
                if (video && video.duration && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
                  setDuration(video.duration);
                }
                if (video && video.currentTime !== undefined && !isNaN(video.currentTime)) {
                  setCurrentTime(video.currentTime);
                }
              }}
              onDurationChange={(e) => {
                const video = e.target;
                if (video && video.duration && !isNaN(video.duration) && isFinite(video.duration) && video.duration > 0) {
                  setDuration(video.duration);
                }
              }}
              onPlaying={() => {
                // Ensure state syncs when video starts playing
                const video = videoRef.current;
                if (video && video.currentTime !== undefined && !isNaN(video.currentTime)) {
                  setCurrentTime(video.currentTime);
                }
                setPlaying(true);
              }}
              onPause={() => {
                // Ensure state syncs when video pauses
                const video = videoRef.current;
                if (video && video.currentTime !== undefined && !isNaN(video.currentTime)) {
                  setCurrentTime(video.currentTime);
                }
                setPlaying(false);
              }}
              aria-label={`Tutorial video: ${title}`}
            >
              {/* Captions track - if video has captions file, add it here */}
              {/* <track kind="captions" srcLang="en" src="/path/to/captions.vtt" default /> */}
            </video>
            <ControlsOverlay show={(showControls || !playing) && !videoEnded}>
              <Stack spacing={1}>
                <Slider
                  value={currentTime}
                  min={0}
                  max={duration || 0}
                  onChange={handleSeek}
                  sx={{
                    color: '#fff',
                    height: 6,
                    '& .MuiSlider-thumb': {
                      width: 14,
                      height: 14,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        width: 16,
                        height: 16,
                        boxShadow: '0 0 0 8px rgba(255, 255, 255, 0.16)'
                      }
                    },
                    '& .MuiSlider-track': {
                      height: 4,
                      borderRadius: 2
                    },
                    '& .MuiSlider-rail': {
                      height: 4,
                      opacity: 0.3,
                      borderRadius: 2
                    }
                  }}
                  aria-label="Video progress"
                />
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <ControlButton onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
                      {playing ? <PauseIcon /> : <PlayIcon />}
                    </ControlButton>
                    <ControlButton onClick={() => setMuted(!muted)} aria-label={muted ? 'Unmute' : 'Mute'}>
                      {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </ControlButton>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: '#fff', 
                        minWidth: 120,
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        letterSpacing: '0.5px'
                      }}
                    >
                      {formatTime(currentTime)} / {duration > 0 ? formatTime(duration) : '--:--'}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ControlButton 
                      onClick={handleSpeedMenuOpen} 
                      aria-label={`Playback speed: ${playbackRate}x`}
                      sx={{
                        minWidth: 'auto',
                        padding: '6px 12px',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      }}
                    >
                      {playbackRate}x
                    </ControlButton>
                    <Menu
                      anchorEl={speedMenuAnchor}
                      open={Boolean(speedMenuAnchor)}
                      onClose={handleSpeedMenuClose}
                      MenuListProps={{
                        sx: {
                          zIndex: 10002,
                        },
                      }}
                      PaperProps={{
                        sx: {
                          bgcolor: 'rgba(0, 0, 0, 0.9)',
                          color: '#fff',
                          minWidth: 120,
                          zIndex: 10002,
                          '& .MuiMenuItem-root': {
                            color: '#fff',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.1)',
                            },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.25)',
                              },
                            },
                          },
                        },
                      }}
                      sx={{
                        zIndex: 10002,
                      }}
                    >
                      {speedOptions.map((speed) => (
                        <MenuItem
                          key={speed}
                          selected={playbackRate === speed}
                          onClick={() => handleSpeedChange(speed)}
                        >
                          {speed}x
                        </MenuItem>
                      ))}
                    </Menu>
                    <ControlButton onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                      {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    </ControlButton>
                  </Box>
                </Box>
              </Stack>
            </ControlsOverlay>
            
            {/* Video Ended Overlay - appears inside video container when video ends */}
            {videoEnded && (
              <NextVideoOverlay className="next-video-overlay">
                <NextVideoContent>
                  {nextTutorial ? (
                    <>
                      <NextVideoInfo>
                        <NextVideoLabel>
                          <SkipNextIcon sx={{ fontSize: 16 }} />
                          Up Next
                        </NextVideoLabel>
                        <NextVideoTitle>
                          {nextTutorial.title}
                        </NextVideoTitle>
                        {nextTutorial.description && (
                          <NextVideoDescription>
                            {nextTutorial.description}
                          </NextVideoDescription>
                        )}
                      </NextVideoInfo>
                      <NextVideoActions>
                        <ReplayButton
                          variant="outlined"
                          startIcon={<ReplayIcon />}
                          onClick={handleReplay}
                        >
                          Replay
                        </ReplayButton>
                        <NextVideoButton
                          variant="contained"
                          startIcon={<PlayIcon />}
                          onClick={handleNextVideo}
                        >
                          Play next video
                        </NextVideoButton>
                      </NextVideoActions>
                    </>
                  ) : (
                    <>
                      <NextVideoInfo>
                        <NextVideoLabel>
                          Video Complete
                        </NextVideoLabel>
                        <NextVideoTitle>
                          {title}
                        </NextVideoTitle>
                      </NextVideoInfo>
                      <ReplayButton
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        onClick={handleReplay}
                      >
                        Replay
                      </ReplayButton>
                    </>
                  )}
                </NextVideoContent>
              </NextVideoOverlay>
            )}
          </VideoContainer>
          
          <TutorialTip sx={{ marginBottom: 2 }} onClose={onClose} />         
        </Box>
      </DialogContent>
    </StyledDialog>
  );
};

export default TutorialVideoPlayer;

